import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { RESOURCE_TOGGLE_TYPE } from './ActivityResourceToggle';
import { AppStoreState, Workbook } from 'types';
import { getFilteredResources } from 'state/modules/entity/selectors/resource-selectors';
import {
  fetchResources,
  fetchFavoriteResources
} from 'state/modules/entity/custom/resource-favorites-mutations/thunks/resource-thunks';
import { filterResources } from 'util/resource-util';
import { getFilteredClickAways } from 'state/modules/user/selectors/click-away-selectors';
import { fetchClickAways } from 'state/modules/user/thunks/user-thunks';
import { getLocale } from 'translations/translator';

interface CurrentWorkbook extends Workbook {
  productCurriculumId: number;
}

const ALL_RESOURCES = 'allResources';

export const ActivityModalContext = React.createContext<any>(null);

export const ActivityModalProvider = ({ children }) => {
  const dispatch = useDispatch() as any;
  const [selectedItems, setSelectedItems] = React.useState<any>([]);
  const [toggledActivityType, setToggledActivityType] = React.useState(
    RESOURCE_TOGGLE_TYPE.HWT_RESOURCES
  );

  const currentWorkbook = useSelector(
    ({ app }: AppStoreState) =>
      ({
        productCurriculumId: app.currentWorkbook?.products[0].productCurriculumId,
        ...app.currentWorkbook
      } as CurrentWorkbook)
  );

  const resources = useSelector(({ entity }: AppStoreState) => entity.resources);
  const favoriteResources = useSelector(({ entity }: AppStoreState) => entity.favoriteResources);
  const selectedFilters = useSelector(
    ({ app: { resource } }: AppStoreState) => resource.selectedFilters
  );

  const filteredResources = useSelector((state: AppStoreState) =>
    getFilteredResources({ ...state.entity, ...state.app }, ALL_RESOURCES)
  );

  const filteredClickaways = useSelector((state: AppStoreState) =>
    getFilteredClickAways(state, ALL_RESOURCES)
  );

  const favoritesOrClickaways = favoriteResources.map(({ clickAway, resource }) =>
    clickAway ? clickAway : resource
  );

  useEffect(() => {
    dispatch(fetchFavoriteResources(currentWorkbook.productCurriculumId));
    if (
      resources?.some(x => !x?.data?.src?.includes(getLocale().toLowerCase())) ||
      !resources.length
    ) {
      dispatch(fetchResources(currentWorkbook?.id));
    }

    if (
      filteredClickaways?.some(x => !x?.data?.src?.includes(getLocale().toLowerCase())) ||
      !filteredClickaways.length
    ) {
      dispatch(fetchClickAways(currentWorkbook?.id));
    } // eslint-disable-next-line
  }, [dispatch]);

  useEffect(() => {
    if (
      toggledActivityType === RESOURCE_TOGGLE_TYPE.HWT_RESOURCES &&
      (filteredResources.length || filteredClickaways.length)
    ) {
      const items = [...filteredResources, ...filteredClickaways];

      setSelectedItems(items);
    }
  }, [filteredResources, filteredClickaways, toggledActivityType]);

  useEffect(() => {
    if (toggledActivityType === RESOURCE_TOGGLE_TYPE.PINNED_RESOURCES) {
      const filteredFavorites = filterResources(
        favoritesOrClickaways,
        selectedFilters.selectedTags,
        ''
      );

      setSelectedItems(filteredFavorites);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggledActivityType]);

  return (
    <ActivityModalContext.Provider
      value={[
        { selectedItems, toggledActivityType },
        { setSelectedItems, setToggledActivityType }
      ]}
    >
      {children}
    </ActivityModalContext.Provider>
  );
};

export const useActivityModalProvider = () => {
  const [{ selectedItems, toggledActivityType }, { setSelectedItems, setToggledActivityType }] =
    React.useContext(ActivityModalContext);
  return [
    { selectedItems, toggledActivityType },
    { setSelectedItems, setToggledActivityType }
  ];
};
